/**
 * For tracking and cancelling HTTP requests by namespace
 * Enables model-level request cancellation for DVA effects
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const CLEANUP_INTERVAL = 60 * 1000; // 1 minute

class RequestTracker {
  constructor() {
    /** @type {Map<string, Map<string, { controller: AbortController, url: string, createdAt: number }>>} */
    this.registry = new Map();

    /** @type {Map<string, boolean>} */
    this.enabledNamespaces = new Map();

    /** @type {number} */
    this.ttl = DEFAULT_TTL;

    // Start cleanup interval
    this._cleanupInterval = setInterval(() => this._cleanupStaleEntries(), CLEANUP_INTERVAL);
  }

  _generateRequestId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get or create namespace registry
   * @param {string} namespace - Namespace identifier
   * @returns {Map<string, { controller: AbortController, url: string, createdAt: number }>}
   * @private
   */
  _getNamespaceRegistry(namespace) {
    if (!this.registry.has(namespace)) {
      this.registry.set(namespace, new Map());
    }
    return this.registry.get(namespace);
  }

  /**
   * Register a request with the tracker
   * @param {string} namespace - Model namespace
   * @param {AbortController} controller - AbortController for the request
   * @param {string} url - Request URL
   * @returns {string} Generated request ID
   */
  register(namespace, controller, url) {
    if (!this.isEnabled(namespace)) {
      return null;
    }

    const requestId = this._generateRequestId();
    const namespaceRegistry = this._getNamespaceRegistry(namespace);

    namespaceRegistry.set(requestId, {
      controller,
      url,
      createdAt: Date.now(),
    });

    return requestId;
  }

  /**
   * Unregister a completed request
   * @param {string} namespace - Model namespace
   * @param {string} requestId - Request identifier to unregister
   * @returns {boolean} True if request was found and removed
   */
  unregister(namespace, requestId) {
    if (!requestId || !namespace) {
      return false;
    }

    const namespaceRegistry = this.registry.get(namespace);
    if (!namespaceRegistry) {
      return false;
    }

    return namespaceRegistry.delete(requestId);
  }

  /**
   * Cancel all active requests for a namespace
   * @param {string} namespace - Model namespace
   * @returns {number} Number of requests cancelled
   */
  cancelAll(namespace) {
    const namespaceRegistry = this.registry.get(namespace);
    if (!namespaceRegistry) {
      return 0;
    }

    let cancelledCount = 0;

    namespaceRegistry.forEach(({ controller }) => {
      try {
        controller.abort();
        cancelledCount++;
      } catch {
        // Silent abort - ignore AbortError
      }
    });

    namespaceRegistry.clear();
    return cancelledCount;
  }

  /**
   * Cancel a specific request
   * @param {string} namespace - Model namespace
   * @param {string} requestId - Request identifier to cancel
   * @returns {boolean} True if request was found and cancelled
   */
  cancelRequest(namespace, requestId) {
    const namespaceRegistry = this.registry.get(namespace);
    if (!namespaceRegistry) {
      return false;
    }

    const requestInfo = namespaceRegistry.get(requestId);
    if (!requestInfo) {
      return false;
    }

    try {
      requestInfo.controller.abort();
    } catch {
      // Silent abort - ignore AbortError
    }

    return namespaceRegistry.delete(requestId);
  }

  getActiveCount(namespace) {
    const namespaceRegistry = this.registry.get(namespace);
    return namespaceRegistry ? namespaceRegistry.size : 0;
  }

  getActiveRequests(namespace) {
    const namespaceRegistry = this.registry.get(namespace);
    if (!namespaceRegistry) {
      return [];
    }

    const requests = [];
    namespaceRegistry.forEach(({ url, createdAt }, requestId) => {
      requests.push({ requestId, url, createdAt });
    });

    return requests;
  }

  isEnabled(namespace) {
    if (!this.enabledNamespaces.has(namespace)) {
      return true; // Default enabled
    }
    return this.enabledNamespaces.get(namespace);
  }

  setEnabled(namespace, enabled) {
    this.enabledNamespaces.set(namespace, enabled);
    // If disabling, cancel and clear existing requests
    if (!enabled) this.cancelAll(namespace);
  }

  _cleanupStaleEntries() {
    const now = Date.now();

    this.registry.forEach((namespaceRegistry, namespace) => {
      const staleIds = [];

      namespaceRegistry.forEach(({ createdAt }, requestId) => {
        if (now - createdAt > this.ttl) {
          staleIds.push(requestId);
        }
      });

      staleIds.forEach((requestId) => {
        const requestInfo = namespaceRegistry.get(requestId);
        if (requestInfo) {
          try {
            requestInfo.controller.abort();
          } catch {
            // Silent abort
          }
          namespaceRegistry.delete(requestId);
        }
      });

      if (namespaceRegistry.size === 0) {
        this.registry.delete(namespace);
      }
    });
  }

  destroy() {
    if (this._cleanupInterval) {
      clearInterval(this._cleanupInterval);
      this._cleanupInterval = null;
    }

    // Cancel all requests across all namespaces
    this.registry.forEach((_, namespace) => {
      this.cancelAll(namespace);
    });

    this.registry.clear();
    this.enabledNamespaces.clear();
  }
}

let instance;

RequestTracker.getInstance = () => {
  if (!instance) instance = new RequestTracker();
  return instance;
};

RequestTracker.resetInstance = () => {
  if (instance) {
    instance.destroy();
    instance = null;
  }
};

export default RequestTracker;
